"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Info_1 = __importDefault(require("@mui/icons-material/Info"));
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lodash_1 = __importDefault(require("lodash"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const shallow_1 = require("zustand/shallow");
const appConstants_1 = require("../../../../constants/appConstants");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const StyledBox_1 = require("../../../../material-ui-theme/components/styled/StyledBox");
const codingQuery_1 = require("../../../../queries/codingQuery");
const companyQuery_1 = require("../../../../queries/companyQuery");
const rolesQuery_1 = require("../../../../queries/rolesQuery");
const teamQuery_1 = require("../../../../queries/teamQuery");
const userQuery_1 = require("../../../../queries/userQuery");
const codingRepository_1 = __importDefault(require("../../../../repositories/codingRepository"));
const companyNameValidator_1 = require("../../../../validators/companyNameValidator");
const zustandStore_1 = require("../../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../../atoms/button/simpleButton"));
const typeInputSetting_1 = __importDefault(require("../../../molecules/setting/typeInputSetting"));
const listTag_1 = __importDefault(require("../../../molecules/tag/listTag"));
const companySettingsAsides_1 = __importDefault(require("./companySettingsAsides"));
const leftSideCompanyFeatures_1 = __importDefault(require("./leftSideCompanyFeatures/leftSideCompanyFeatures"));
const rightSideCompanyFeatures_1 = __importDefault(require("./rightSideCompanyFeatures"));
const logger = (0, pino_1.default)();
const validateCompanyName = name => {
    if (lodash_1.default.isEmpty(name)) {
        return { valid: false, errorMessage: strings_1.validationStrings.REQUIRED_FIELD_ERROR };
    }
    else if (!(0, companyNameValidator_1.isCompanyNameLengthValid)(name)) {
        return { valid: false, errorMessage: strings_1.companySettingsOverviewStrings.COMPANY_NAME_LENGTH_ERROR };
    }
    return { valid: true, errorMessage: '' };
};
const onCompanyNameChange = ({ setIsCompanySettingsSaveBtnDisabled, setCompanyName, companyErrors, setCompanyErrors, value, }) => {
    setIsCompanySettingsSaveBtnDisabled(false);
    setCompanyName(value);
    const validName = validateCompanyName(value);
    if (validName.valid) {
        const { [strings_1.companySettingsOverviewStrings.COMPANY_NAME_LABEL]: omitted, ...errors } = companyErrors;
        setCompanyErrors(errors);
        setIsCompanySettingsSaveBtnDisabled(Object.keys(errors).length > 0);
    }
    else {
        setCompanyErrors({
            ...companyErrors,
            [strings_1.companySettingsOverviewStrings.COMPANY_NAME_LABEL]: validName.errorMessage,
        });
        setIsCompanySettingsSaveBtnDisabled(true);
    }
};
const CompanySettingsOverviewTemplate = () => {
    const { user, setIsAddAdminAsideOpen, setIsInvitePeopleAsideOpen, company, setTeamOwners, teamOwners, setNotification, setIsCompanySettingsSaveBtnDisabled, setCompanyErrors, setCompanyName, companyErrors, setUserSelectedTeam, setCompanySyncState, } = (0, zustandStore_1.useZustand)((0, shallow_1.useShallow)(state => ({
        user: state.user,
        company: state.company,
        setIsAddAdminAsideOpen: state.setIsAddAdminAsideOpen,
        setIsInvitePeopleAsideOpen: state.setIsInvitePeopleAsideOpen,
        setTeamOwners: state.setTeamOwners,
        teamOwners: state.team.owners,
        setNotification: state.setNotification,
        setIsCompanySettingsSaveBtnDisabled: state.setIsCompanySettingsSaveBtnDisabled,
        setCompanyErrors: state.setCompanyErrors,
        companyErrors: state.company.companyErrors,
        setCompanyName: state.setCompanyName,
        setUserSelectedTeam: state.setUserSelectedTeam,
        setCompanySyncState: state.setCompanySyncState,
    })));
    const initialData = (0, react_router_1.useLoaderData)();
    const saveCompanyName = (0, companyQuery_1.useSaveCompanyNameQuery)();
    const saveCompanyFeature = (0, companyQuery_1.useSaveCompanyFeatureQuery)();
    const saveProviderMappingQuery = (0, codingQuery_1.useSaveProviderMappingQuery)();
    const saveProviderFieldsQuery = (0, codingQuery_1.useSaveProviderFieldsQuery)();
    const deleteProviderMappingQuery = (0, codingQuery_1.useDeleteProviderMappingQuery)();
    const saveCompanyProperty = (0, companyQuery_1.useSaveCompanyPropertyQuery)();
    const { data: userData } = (0, react_query_1.useQuery)(userQuery_1.userQuery);
    const { data: userSelectedTeamData } = (0, react_query_1.useQuery)((0, teamQuery_1.selectedTeamIdQuery)('selectedTeamID'));
    const removeRoleFromUserMutation = (0, rolesQuery_1.useRemoveRoleFromUserQuery)(appConstants_1.overviewConstants.COMPANY_ADMIN);
    const [hasChanges, setHasChanges] = (0, react_1.useState)(false);
    const [selectedUser, setSelectedUser] = (0, react_1.useState)(null);
    const [openRemoveUserAside, setOpenRemoveUserAside] = (0, react_1.useState)(false);
    const { data: companyAdminUsers } = (0, userQuery_1.useGetUsersByRoleId)(appConstants_1.overviewConstants.COMPANY_ADMIN);
    const { data: companyPropertiesData } = (0, react_query_1.useQuery)({
        ...(0, companyQuery_1.companyPropertiesQuery)(user.companyAccountId, { cardprovider: 'tsgo-cardprovider' }),
        initialData: initialData?.companyProperties,
    });
    const isHsbcCardProvider = companyPropertiesData?.cardprovider === 'HSBC';
    const theme = (0, material_1.useTheme)();
    const onChanges = (callback, key, value) => {
        if (!hasChanges) {
            setHasChanges(true);
        }
        callback(key, value);
    };
    const onSaveChangesClick = () => {
        setHasChanges(false);
        void saveChanges({
            company,
            user,
            saveProviderMappingQuery,
            saveProviderFieldsQuery,
            deleteProviderMappingQuery,
            saveCompanyFeature,
            saveCompanyProperty,
            userData,
            saveCompanyName,
            setNotification,
        });
        setIsCompanySettingsSaveBtnDisabled(true);
        const { companyName, propertyValue, features, properties } = company;
        const syncState = {
            companyName,
            propertyValue,
            features,
            properties,
        };
        setCompanySyncState(syncState);
    };
    const toggleRemoveAdminAside = (id) => {
        setSelectedUser(companyAdminUsers?.users.find(item => item.userId === id) || {});
        setOpenRemoveUserAside(!openRemoveUserAside);
    };
    const onRemoveAdmin = (0, react_1.useCallback)(() => {
        removeRoleFromUserMutation.mutate({
            userId: selectedUser?.userId,
            roleId: appConstants_1.overviewConstants.COMPANY_ADMIN,
            securityGroupId: selectedUser.companyAccountId,
        }, {
            onSuccess: () => {
                setNotification([
                    {
                        type: strings_1.notificationStrings.SUCCESS,
                        message: strings_1.companySettingsOverviewStrings.REMOVE_ADMIN_SUCCESS,
                    },
                ]);
                setTeamOwners([...teamOwners, selectedUser]);
            },
            onError: error => {
                logger.error({ error });
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: strings_1.companySettingsOverviewStrings.REMOVE_ADMIN_ERROR,
                    },
                ]);
            },
            onSettled: () => {
                setOpenRemoveUserAside(false);
            },
        });
    }, [removeRoleFromUserMutation, selectedUser, setNotification, setTeamOwners, teamOwners]);
    (0, react_1.useEffect)(() => {
        setUserSelectedTeam(userSelectedTeamData?.[0] || '');
    }, [userSelectedTeamData, setUserSelectedTeam]);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(material_1.Paper, { "ts-variant": "container", children: (0, jsx_runtime_1.jsxs)(StyledBox_1.StyledBox, { children: [(0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, children: [(0, jsx_runtime_1.jsxs)(material_1.Grid, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, size: 12, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "h3", children: strings_1.companySettingsOverviewStrings.HEADER_TITLE }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "saveChanges-btn", label: strings_1.companySettingsOverviewStrings.HEADER_BUTTON_NAME, onClick: () => onSaveChangesClick(), disabled: company.uiSettings.saveButton.isDisabled, color: uiConstants_1.UI_BUTTON_PRIMARY })] }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Divider, { "ts-variant": "dotted" }) })] }), (0, jsx_runtime_1.jsxs)(material_1.Box, { component: "form", autoComplete: "off", style: {
                                margin: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                    ? `40px ${theme.tsGoUnit.tsUnitHalf}`
                                    : theme.tsGoUnit.tsUnitHalf,
                            }, sx: {
                                overflow: 'hidden',
                            }, children: [(0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, rowSpacing: 1, style: { width: '50%', maxWidth: '100%', display: 'flex', flexDirection: 'column' }, children: [(0, jsx_runtime_1.jsxs)(material_1.Grid, { sx: {
                                                pb: 1,
                                            }, size: "grow", children: [(0, jsx_runtime_1.jsx)(typeInputSetting_1.default, { dataTestId: "companyName-input", name: strings_1.companySettingsOverviewStrings.COMPANY_NAME_LABEL, value: company.companyName, title: strings_1.companySettingsOverviewStrings.COMPANY_NAME_LABEL, onValueChange: (_key, value) => onCompanyNameChange({
                                                        setIsCompanySettingsSaveBtnDisabled,
                                                        setCompanyName,
                                                        companyErrors,
                                                        setCompanyErrors,
                                                        value,
                                                    }), errorMessage: companyErrors[strings_1.companySettingsOverviewStrings.COMPANY_NAME_LABEL], label: "Company Name", maxLength: 40 }), company.companyName.length >= 40 ? ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { display: 'flex', paddingLeft: '9px' }, children: [(0, jsx_runtime_1.jsx)(Info_1.default, { color: "info", fontSize: "small", style: { verticalAlign: 'middle' } }), (0, jsx_runtime_1.jsx)(material_1.Typography, { component: "span", sx: { color: '#0f1519', fontSize: '12px', paddingLeft: '5px' }, children: strings_1.companySettingsOverviewStrings.COMPANY_NAME_LENGTH_ERROR })] })) : null] }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: "grow", children: (0, jsx_runtime_1.jsxs)(material_1.Box, { style: {
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '0 10px',
                                                    gap: '20px',
                                                    ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                        ? {
                                                            paddingLeft: '0',
                                                        }
                                                        : {}),
                                                }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "span", style: {
                                                            ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                                ? {
                                                                    color: 'black',
                                                                    fontSize: '12px',
                                                                    fontWeight: 400,
                                                                    textTransform: 'none',
                                                                }
                                                                : {}),
                                                        }, children: strings_1.companySettingsOverviewStrings.COMPANY_ADMINS_LABEL }), isHsbcCardProvider ? null : ((0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "addCompanyName-btn", color: uiConstants_1.UI_BUTTON_SECONDARY, size: 'small', label: strings_1.companySettingsOverviewStrings.ADD_COMPANY_ADMIN_BUTTON, onClick: () => {
                                                            if (teamOwners.length > 0) {
                                                                setIsAddAdminAsideOpen(true);
                                                            }
                                                            setIsInvitePeopleAsideOpen(teamOwners.length === 0);
                                                        } }))] }) }), companyAdminUsers && Array.isArray(companyAdminUsers.users) && companyAdminUsers.users.length > 0 && ((0, jsx_runtime_1.jsx)(material_1.Grid, { sx: { display: 'flex', flex: '0.5', marginBottom: '20px' }, size: "grow", children: (0, jsx_runtime_1.jsx)(listTag_1.default, { style: {
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    paddingRight: '0px',
                                                    gap: '5px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                                        ? {
                                                            padding: 0,
                                                        }
                                                        : {}),
                                                }, labels: companyAdminUsers?.users.map(admin => ({
                                                    key: `${lodash_1.default.escape(admin.userId)}`,
                                                    label: `${lodash_1.default.escape(admin.fullName?.toUpperCase())} (${admin.email?.toUpperCase()})`,
                                                    disabledRemove: admin?.userId === user?.['userId'],
                                                })), onClick: ((key, value) => onChanges(toggleRemoveAdminAside, key, value)) }) }))] }), (0, jsx_runtime_1.jsx)(material_1.Grid, { size: 12, children: (0, jsx_runtime_1.jsx)(material_1.Divider, { "ts-variant": "dotted" }) }), (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, style: {
                                        flexWrap: 'nowrap',
                                        ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                                            ? {
                                                gap: '20px',
                                            }
                                            : {}),
                                    }, sx: {
                                        mt: 1,
                                    }, children: [(0, jsx_runtime_1.jsx)(leftSideCompanyFeatures_1.default, {}), (0, jsx_runtime_1.jsx)(material_1.Grid, { style: { maxWidth: '1px' }, size: 1, children: (0, jsx_runtime_1.jsx)(material_1.Divider, { orientation: "vertical", style: { height: '100%' }, "ts-variant": "dotted" }) }), (0, jsx_runtime_1.jsx)(rightSideCompanyFeatures_1.default, {})] })] })] }) }), (0, jsx_runtime_1.jsx)(companySettingsAsides_1.default, { openRemoveUserAside: openRemoveUserAside, selectedUser: selectedUser, toggleRemoveAdminAside: toggleRemoveAdminAside, onRemoveAdmin: onRemoveAdmin })] }));
};
exports.default = CompanySettingsOverviewTemplate;
async function saveChanges({ company, user, saveProviderMappingQuery, saveProviderFieldsQuery, deleteProviderMappingQuery, saveCompanyFeature, saveCompanyProperty, userData, saveCompanyName, setNotification, }) {
    const mutationPromises = [];
    const defaultCompanyFeatures = Object.values(appConstants_1.companyFeaturesConstants);
    for (const feature of defaultCompanyFeatures) {
        if (company.features) {
            if ([appConstants_1.companyFeaturesConstants.enableCompanyPeopleView, appConstants_1.companyFeaturesConstants.enableGlobalization].includes(feature) &&
                !user.isSupportAdmin) {
                continue;
            }
            if (feature === appConstants_1.companyFeaturesConstants.enableProviderMapping) {
                if (company.features[feature]) {
                    mutationPromises.push(saveProviderMappingQuery.mutateAsync('AMEX'));
                }
                else {
                    mutationPromises.push(deleteProviderMappingQuery.mutateAsync(), saveProviderFieldsQuery.mutateAsync({ providerFields: codingRepository_1.default.DEFAULT_PROVIDER_MAPPING }));
                }
            }
            else {
                mutationPromises.push(saveCompanyFeature.mutateAsync({
                    key: feature,
                    value: company?.features?.[feature] || '',
                }));
            }
        }
    }
    for (const [key, value] of Object.entries(appConstants_1.companyPropertiesConstants)) {
        const compPropValue = lodash_1.default.isObject(company.properties[key]) && lodash_1.default.isEmpty(company.properties[key]) ? '' : company.properties[key];
        if (compPropValue == null) {
            continue;
        }
        if (value === appConstants_1.companyPropertiesConstants.defaultExpirationDate && !compPropValue) {
            // fails on backend if is falsy
            continue;
        }
        mutationPromises.push(saveCompanyProperty.mutateAsync({
            companyId: company.companyId,
            key: value,
            value: compPropValue,
        }));
    }
    if (company.companyName !== userData?.companyName) {
        mutationPromises.push(saveCompanyName.mutateAsync({ companyName: company.companyName }));
    }
    const results = await Promise.allSettled(mutationPromises);
    const failedMutations = results.filter(result => result.status === 'rejected');
    if (failedMutations.length === 0) {
        setNotification([
            {
                type: strings_1.notificationStrings.SUCCESS,
                message: strings_1.companySettingsOverviewStrings.COMPANY_SETTINGS_SUCCESS,
            },
        ]);
    }
    else {
        setNotification([
            {
                type: strings_1.notificationStrings.ERROR,
                message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                    ? strings_1.errorStrings.DEFAULT
                    : strings_1.companySettingsOverviewStrings.COMPANY_SETTINGS_ERROR,
            },
        ]);
    }
}
